import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import Spinner from 'app/components/Spinner'
import { Button } from 'app/components/ui/button'
import { useAuthStore } from 'app/stores/StoreContext'
import { safeRedirect } from 'app/utils'
import { getCookie } from 'lib/getCookie'
import { logError } from 'lib/logging'
import { notifyError } from 'app/services/notify.client'

const REDIRECT_URI = `${window.location.origin}/auth/google/callback`

export default observer(function GoogleCallbackPage(): React.ReactElement {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const auth = useAuthStore()
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    const code = searchParams.get('code')
    const oauthError = searchParams.get('error')

    if (oauthError) {
      setError(`Google sign-in was cancelled or failed: ${oauthError}`)
      return
    }

    if (!code) {
      setError('No authorization code received from Google')
      return
    }

    handleGoogleCode(code)
  }, [])

  async function handleGoogleCode(code: string) {
    try {
      const result = await auth.loginWithGoogleCode(code, REDIRECT_URI)
      if (!result) {
        setError(auth.error || 'Google sign-in failed')
        return
      }
      if (result.accessToken) {
        await auth.fetchMe()
        const returnTo = getCookie<string>('returnTo') || '/'
        navigate(safeRedirect(returnTo))
      } else if (result.pendingToken) {
        // User has MFA enabled — redirect to login with OTP state
        // For now, show a message; the login page handles OTP
        navigate('/login')
        notifyError('Please complete two-factor authentication')
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Google sign-in failed'
      logError('Google OAuth callback failed', err)
      setError(msg)
    }
  }

  return (
    <div className="grid h-dvh place-items-center overflow-y-auto bg-surface-subtle p-4">
      {error ? (
        <div className="w-full max-w-lg rounded-xl border border-default bg-surface p-8 text-center shadow-sm sm:p-12">
          <h3 className="mb-3 text-2xl font-semibold text-strong">Something went wrong</h3>
          <p className="mb-8 italic text-subtle" role="alert">
            {error}
          </p>
          <Button variant="primary" size="lg" asChild className="w-full">
            <Link to="/login">Back to login</Link>
          </Button>
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  )
})
